import type { FetchError } from '@execonline-inc/fetch.private';
import type { UseFetch } from '@execonline-inc/react-hooks.private';
import { fromNullable, just, Maybe, nothing } from 'maybeasy';
import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import type { MissingLinkError } from '../../../../../Links';
import * as Resourceable from '../../../../../Resourceable';
import { useLicenseOptions } from './useLicenseOptions';

// DVLP-7601: shared preview state — which simulated PLT the admin picked, the
// PLT option list itself (fetched once here so every consumer sees the same
// data), and whether unlicensed programs are visible. Provided by
// AdminPreview, consumed by PreviewLicenseSelector, UnlockedCount,
// LockedCoursesToggle, and the
// preview builder's card filter.

// 'Active'    — org holds a license of this type that is live today
// 'Pending'   — org holds one that has not started yet (grouped with Active)
// 'Available' — commercially available, not held (upsell)
export type LicenseOptionStatus = 'Active' | 'Pending' | 'Available';

export interface LicenseOption {
  product_license_type_id: number;
  name: string;
  status: LicenseOptionStatus;
  display_order: number;
  unlocks_count: number;
  // DVLP-8018: the same for every option — the courses the admin added plus
  // the On-Demand catalog when nothing is attached. The denominator never
  // moves with the dropdown; the license only decides what is locked.
  total_count: number;
  // Program family IDs this PLT would entitle in this collection — drives
  // the licensed-vs-unlicensed classification of every card.
  entitled_experience_ids: number[];
}

type OptionsFetchState = UseFetch.State<
  FetchError | MissingLinkError,
  Resourceable.ParsedResponse<'/preview_license_options', 'get'>
>;

interface PreviewLicenseState {
  selectedPltId: Maybe<number>;
  // The option record behind selectedPltId, once options have loaded.
  selectedOption: Maybe<LicenseOption>;
  setSelectedPltId: (id: number) => void;
  showUnlicensed: boolean;
  setShowUnlicensed: (v: boolean) => void;
  learningCollectionId: string;
  optionsState: OptionsFetchState;
  options: LicenseOption[];
  // Set of program family IDs entitled under the currently selected PLT.
  // Empty when no PLT is selected or the fetch hasn't landed.
  entitledExperienceIds: Set<number>;
}

const noop = () => {};

const PreviewLicenseContext = createContext<PreviewLicenseState>({
  selectedPltId: nothing(),
  selectedOption: nothing(),
  setSelectedPltId: noop,
  showUnlicensed: false,
  setShowUnlicensed: noop,
  learningCollectionId: '',
  optionsState: { kind: 'loading' } as OptionsFetchState,
  options: [],
  entitledExperienceIds: new Set(),
});

export const usePreviewLicense = () => useContext(PreviewLicenseContext);

interface ProviderProps {
  learningCollectionId: string;
  children: ReactNode;
}

export const PreviewLicenseProvider = ({ learningCollectionId, children }: ProviderProps) => {
  const [selected, setSelected] = useState<Maybe<number>>(nothing());
  // Hidden by default — admins usually want the licensed-only view first,
  // then opt in to see what's excluded.
  const [showUnlicensed, setShowUnlicensed] = useState<boolean>(false);

  const optionsState = useLicenseOptions(learningCollectionId);

  const options: LicenseOption[] = useMemo(
    () => (optionsState.kind === 'ready' ? optionsState.data.resource.payload.options : []),
    [optionsState],
  );

  const selectedOption = useMemo(
    () =>
      selected.andThen((id) => fromNullable(options.find((o) => o.product_license_type_id === id))),
    [selected, options],
  );

  const entitledExperienceIds = useMemo(
    () =>
      selectedOption
        .map((o) => new Set(o.entitled_experience_ids))
        .getOrElse(() => new Set<number>()),
    [selectedOption],
  );

  const value = useMemo(
    () => ({
      selectedPltId: selected,
      selectedOption,
      setSelectedPltId: (id: number) => setSelected(just(id)),
      showUnlicensed,
      setShowUnlicensed,
      learningCollectionId,
      optionsState,
      options,
      entitledExperienceIds,
    }),
    [
      selected,
      selectedOption,
      showUnlicensed,
      learningCollectionId,
      optionsState,
      options,
      entitledExperienceIds,
    ],
  );

  return <PreviewLicenseContext.Provider value={value}>{children}</PreviewLicenseContext.Provider>;
};
